import {DomainObject} from 'shared/store';
import {observable, action, computed} from 'mobx';
import CoverageVolumeTier from './CoverageVolumeTier';
import CoverageVolumeReduction from './CoverageVolumeReduction';
import _ from 'lodash';
import {t} from 'shared/core';

const VOLUME_COVERAGES = [
  'life',
  'accidental_death_and_dismemberment',
  'short_term_disability',
  'long_term_disability',
  'critical_illness'
];

const EXPERIENCE_RATED_COVERAGES = [
  'extended_health_care',
  'dental',
  'vision',
  'travel',
  'healthcare_spending_account'
];

const EVIDENCE_MAXIMUM_COVERAGES = [
  'life',
  'accidental_death_and_dismemberment',
  'short_term_disability',
  'long_term_disability',
  'critical_illness'
];

const OPTIONAL_COVERAGES = [
  'optional_life',
  'optional_accidental_death_and_dismemberment',
  'optional_critical_illness',
  'optional_dependent_life',
  'optional_spousal_life',
  'optional_dependent_accidental_death_and_dismemberment'
];

const EXCLUDED_COVERAGES = [
  'flexible_spending_account',
  'wellness_spending_account',
  'management_assistance_program',
  'total_health',
  'stop_loss',
  'people_connect_carepath'
];

const DISABILITY_COVERAGES = [
  'short_term_disability',
  'long_term_disability'
];

class GroupBenefitCoverage extends DomainObject {
  // Common Fields
  @observable coverageType;
  @observable premiumSplitAmount;
  @observable premiumSplitPercentage;
  @observable premiumSplitType;
  @observable terminationAge;
  @observable salesTaxRegion;
  @observable nonEvidenceMaximum;
  @observable evidenceMaximum;
  @observable excludeFromTaxableBenefits;

  // Questionable field
  @observable eligibilityEnabled;

  // Volume Coverages
  @observable coverageVolumeRate;
  @observable coverageRate;
  @observable volumeType;
  @observable volumeMultiple;
  @observable volumeAmount;
  @observable coverageVolumeTiers = [];

  // Other + Dependent Life
  @observable cost;

  // Experience Rated Coverages
  @observable singleCost;
  @observable singleParentCost;
  @observable singleWaivedCost;
  @observable familyCost;
  @observable familyWaivedCost;
  @observable coupleCost;

  // LTD and STD
  @observable taxableOnPayout;

  // Reductions
  @observable coverageVolumeReductions = [];

  @observable descriptions;
  @observable subCategories;
  @observable title;
  @observable tooltip;

  @action merge(other) {
    super.merge(other, {
      coverageVolumeTiers: [CoverageVolumeTier],
      coverageVolumeReductions: [CoverageVolumeReduction]
    });
  }

  @action updateVolumeAmount(value) {
    this.volumeAmount = value;

    if (this.volumeType === 'amount') {
      this.evidenceMaximum = this.volumeAmount || 0;
      this.nonEvidenceMaximum = this.volumeAmount || 0;
    }
  }

  @action updateVolumeType(value) {
    this.volumeType = value;

    if (this.volumeType !== 'amount') {
      this.volumeAmount = null;
      this.evidenceMaximum = 0;
      this.nonEvidenceMaximum = 0;
    }
  }

  @computed get evidenceMaximumRequired() {
    return _.includes(EVIDENCE_MAXIMUM_COVERAGES, this.coverageType);
  }

  @computed get coverageCategory() {
    if (_.includes(VOLUME_COVERAGES, this.coverageType)) {
      return 'volume_rate_coverage';
    } else if (_.includes(EXPERIENCE_RATED_COVERAGES, this.coverageType)) {
      return 'experience_rated_coverage';
    } else if (_.includes(OPTIONAL_COVERAGES, this.coverageType)) {
      return 'optional_coverage';
    } else if (_.includes(EXCLUDED_COVERAGES, this.coverageType)) {
      return 'excluded_coverage';
    } else {
      return 'cost_coverage';
    }
  }

  @computed get isVolumeCoverage() {
    return this.coverageCategory === 'volume_rate_coverage';
  }

  @computed get isOptionalCoverage() {
    return this.coverageCategory === 'optional_coverage';
  }

  @computed get isExperienceRatedCoverage() {
    return this.coverageCategory === 'experience_rated_coverage';
  }

  @computed get isCostCoverage() {
    return this.coverageCategory === 'cost_coverage';
  }

  @computed get isDependentLife() {
    return this.coverageType === 'dependent_life';
  }

  @computed get isOtherCoverage() {
    return this.coverageType === 'other';
  }

  @computed get isDisabilityCoverage() {
    return _.includes(DISABILITY_COVERAGES, this.coverageType);
  }

  @computed get volumeBands() {
    let total = 0;

    const bands = _.orderBy(this.coverageVolumeTiers, 'order').map(tier => {
      const volumeBand = parseInt(tier.volumeBand) || Infinity;
      const range = { min: total, max: total + volumeBand };
      total += volumeBand;
      return range;
    });

    return bands;
  }

  @computed get subCategoriesWithIndex() {
    return _.map(this.subCategories, (category, i) => {
      return {index: i + 1, ...category};
    });
  }

  minRangeForTier(tier) {
    return this.rangeForTier(tier).min;
  }

  maxRangeForTier(tier) {
    const maxRange = this.rangeForTier(tier).max;
    if (maxRange === Infinity) {
      return t('taxable_benefits.Rest of salary');
    }

    return maxRange;
  }

  rangeForTier(tier) {
    return this.volumeBands[tier.order];
  }
}
export default GroupBenefitCoverage;
